import { Text, View, Pressable, Platform } from 'react-native';
import { useState, useEffect, useCallback, useContext } from 'react';
import { Image } from 'expo-image';
import { useRouter, useSegments } from 'expo-router';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import useNotificationBadges from '@/utils/hooks/useNotificationBadges';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import NotificationBadge from '@/components/General/NotificationBadge';
import Unicon from './Unicon';
import SheetModal from './SheetModal';
import SearchModal from './SearchModal';
import usePlatform from '@/utils/hooks/usePlatform';
import NotificationsScreen from '@/app/(tabs)/(home,search,notifications,settings)/notifications';
import { Query } from '@/utils/react-query/Queries';
import { GuideContext, GuidePageContext } from '@/components/Guides/GuideProvider';
import { getGuide, GUIDES_ENABLED } from '@/config/guides';

const WebHeader = () => {
    const [searchOpen, setSearchOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const { theme: { scheme } } = useUnistyles();
    const router = useRouter();
    const segments = useSegments();
    const { isWeb } = usePlatform();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const guide = useContext(GuideContext);
    const guidePage = useContext(GuidePageContext);
    const pageGuide = getGuide(guidePage?.guideID);
    const pageGuideID = guide?.accountID && pageGuide && pageGuide.headerReplay !== false ? pageGuide.id : null;
    const currentTab = segments[0] === '(tabs)' && segments[1] ? segments[1] : '(home)';
    const [searchShortcut, setSearchShortcut] = useState("");

    const { totalUnread: notificationCount } = useNotificationBadges();
    // Notifications are a Paid module — the bell never shows on Free orgs.
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);

    const openSearch = useCallback(() => setSearchOpen(true), []);
    const closeSearch = useCallback(() => setSearchOpen(false), []);
    const openNotifications = useCallback(() => setNotificationsOpen(true), []);
    const closeNotifications = useCallback(() => setNotificationsOpen(false), []);

    useEffect(() => {
        if (Platform.OS !== 'web') return;

        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                openSearch();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [openSearch]);

    useEffect(() => {
        if(isWeb){
            if (navigator.platform.indexOf('Win') !== -1) {
                setSearchShortcut("ctrl + k")
            } else if (navigator.platform.indexOf('Mac') !== -1) {
                setSearchShortcut("cmd + k")
            }
        }
    }, [])

    // Dev-only web watermark: diagonal repeating "TEST" pattern overlaid on the header surface.
    const devWebBg = (__DEV__ && isWeb)
        ? {
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
                "<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><g transform='rotate(-45 80 80)'><text x='-20' y='80' fill='rgba(128,128,128,0.15)' font-size='18' font-family='monospace' letter-spacing='3'>TEST TEST TEST</text></g></svg>"
            )}")`,
            backgroundRepeat: 'repeat',
        }
        : null;

    return (
        <>
            <View style={[styles.header(isWeb), devWebBg]}>
                {/* Left: App logo */}
                <Pressable testID="web-header-logo" onPress={() => router.push("/")} style={styles.logoWrap}>
                    <Image
                        source={scheme == "light" ? require('./../../assets/logo/Azul.png') : require('./../../assets/logo/GhostWhite.png')}
                        style={{height:54, width:108}}
                        contentFit="contain"
                    />
                    {/* <Text style={styles.logoText}>Church Management</Text> */}
                </Pressable>

                {/* Right: Search bar + Notifications */}
                <View style={styles.rightSection}>
                    {GUIDES_ENABLED && pageGuideID && (
                        <Pressable
                            testID="guide-replay-header"
                            accessibilityRole="button"
                            accessibilityLabel="Tour this page"
                            style={styles.iconBtn}
                            onPress={() => guide.replay(pageGuideID)}
                        >
                            <Unicon name="help-circle-outline" size={19} colorVariant="textSecondary" />
                        </Pressable>
                    )}
                    <Pressable testID="web-header-search" style={styles.searchBar} onPress={openSearch}>
                        <Unicon name="search-outline" size={15} colorVariant="textTertiary" />
                        <Text style={styles.searchPlaceholder}>Search anything...</Text>
                        <View style={styles.searchKbd}>
                            <Text style={styles.searchKbdText}>{searchShortcut}</Text>
                        </View>
                    </Pressable>

                    {loggedInUser?.can?.('Use AI Assistant') && (
                        <Pressable
                            testID="web-header-assistant"
                            accessibilityRole="button"
                            accessibilityLabel="Open Assistant"
                            style={styles.iconBtn}
                            onPress={() => router.navigate(`/(tabs)/${currentTab}/assistant`)}
                        >
                            <Unicon name="sparkles-outline" size={19} colorVariant="textSecondary" />
                        </Pressable>
                    )}

                    {!isFree && (
                        <Pressable testID="web-header-notifications" style={styles.iconBtn} onPress={openNotifications}>
                            <Unicon name="notifications-outline" size={19} colorVariant="textSecondary" />
                            <NotificationBadge count={notificationCount} size={16} style={styles.notifBadge} testID="web-header-notifications-badge" />
                        </Pressable>
                    )}
                </View>
            </View>

            <SheetModal title="Search" isOpen={searchOpen} onCancel={closeSearch}>
                <SearchModal onSelect={() => closeSearch()} isOpen={searchOpen} />
            </SheetModal>

            <SheetModal title="Notifications" isOpen={notificationsOpen} onCancel={closeNotifications} unmountOnClose>
                <NotificationsScreen onSelect={() => closeNotifications()} />
            </SheetModal>
        </>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography }) => ({
    header: (isWeb) => ({
        height: isWeb ? 64 : 84,
        paddingTop: isWeb ? 0 : spacing.lg,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.md + 4,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
        boxShadow: shadows.header,
        zIndex: 1001,
    }),
    logoWrap: {
        paddingVertical: spacing.xs,
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
    },
    // logoText: {
    //     fontSize: typography.body,
    //     fontFamily: fonts.body,
    //     color: colors.textSecondary,
    // },
    rightSection: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm + 2,
    },
    searchBar: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        height: 34,
        paddingHorizontal: spacing.sm + 4,
        borderRadius: 8,
        backgroundColor: colors.background,
        borderWidth: 1,
        borderColor: colors.border,
        minWidth: 220,
        _web: {
            cursor: "pointer",
            transition: "all 0.15s ease",
            _hover: { borderColor: colors.bgPrimary, backgroundColor: colors.hover },
        },
    },
    searchPlaceholder: {
        flex: 1,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
    searchKbd: {
        paddingHorizontal: spacing.xs + 2,
        paddingVertical: 2,
        borderRadius: 4,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
    },
    searchKbdText: {
        fontSize: typography.micro,
        fontFamily: 'monospace',
        color: colors.textTertiary,
    },
    iconBtn: {
        width: 34,
        height: 34,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        _web: {
            transition: "background-color 0.12s ease",
            _hover: { backgroundColor: colors.hover },
        },
    },
    notifBadge: {
        position: "absolute",
        top: 0,
        right: 0,
    },
}));

export default WebHeader;
